import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, IconButton, Typography, Icon, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import {
  BarTitle,
  ButtonContainer,
  Container,
  ReassignButton,
  ReassignDialog,
  ReassignToolbar,
  Title,
} from './ReassignModal.styles';
import { get, set, size } from 'global/lib/lodash';
import Alert from '@mui/material/Alert';
import { useScreen, Loader } from 'global/styles';
import sendData from 'redux/actions/sendData';
import Form from 'global/components/form';
import { Row } from '../../../global/components/row';
import { Text } from '../../../global/components/text';
import { TradeSelect } from '../../../global/components/trade-select';
import { CheckpointScore } from '../../report/ChecklistScores';
import { cleanCopy } from '../../../global/utils';
import { getInstanceSubScores } from '../../report/AssessmentScore/utils';
import { SETTINGS, taskOptions } from '../../admin/account-settings';
import { getAccountSetting } from 'redux/actions/userLogin';
import { useTaskTypes } from '../task-type-utils/useTaskTypes';
import { getAvailableTaskTypes, getCustomFieldInputs, getCloseoutFromTaskType, getCloseoutDefaults, getStandardFieldRequirements } from '../task-type-utils';

// The scores list can be filtered to another status (review, target, new target).
// Bulk create always shows the active score for each selected item.
const ACTIVE_SCORE_FILTER = { assessment_status: 'active' };

// eslint-disable-next-line import/no-anonymous-default-export
export default (props) => {
  const theme = useTheme();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState({});
  const [items, setItems] = useState({});
  const [open, setOpen] = useState({});
  const [openHeaderTrades, setOpenHeaderTrades] = useState({});
  const [stepMode, setStepMode] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepFormStates, setStepFormStates] = useState({});
  const itemsRef = useRef({});
  const headerSelectRefs = useRef({});
  const pendingHeaderFocusId = useRef(null);
  const formRef = useRef(null);
  const stepFormRef = useRef(null);

  const { isMobileXS } = useScreen();

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const getTradeIds = (trades = []) =>
    (trades || [])
      .map((trade) => get(trade, 'id'))
      .filter((id) => id !== undefined && id !== null && id !== '')
      .map((id) => String(id));

  const mergeTrades = (...tradeLists) => {
    const seen = new Set();
    const merged = [];

    tradeLists.flat().forEach((trade) => {
      const rawTradeId = get(trade, 'id');
      if (rawTradeId === undefined || rawTradeId === null || rawTradeId === '') {
        return;
      }

      const tradeId = String(rawTradeId);
      if (seen.has(tradeId)) {
        return;
      }

      seen.add(tradeId);
      merged.push(trade);
    });

    return merged;
  };

  // Header chips are the trades present on every item in the group.
  const getSharedTrades = (checkpoints = [], preferredTrades = []) => {
    if (!size(checkpoints)) {
      return [];
    }

    const idSets = checkpoints.map((checkpoint) => new Set(getTradeIds(get(checkpoint, 'tradeTypes', []))));
    const sharedIds = [...idSets[0]].filter((id) => idSets.every((ids) => ids.has(id)));
    if (!size(sharedIds)) {
      return [];
    }

    const sharedIdSet = new Set(sharedIds);
    const byId = new Map();
    [...preferredTrades, ...checkpoints.flatMap((checkpoint) => get(checkpoint, 'tradeTypes', []))].forEach((trade) => {
      const rawId = get(trade, 'id');
      if (rawId === undefined || rawId === null || rawId === '') {
        return;
      }
      const id = String(rawId);
      if (sharedIdSet.has(id) && !byId.has(id)) {
        byId.set(id, trade);
      }
    });

    const ordered = [];
    const seen = new Set();
    const push = (id) => {
      if (!id || seen.has(id) || !byId.has(id)) {
        return;
      }
      seen.add(id);
      ordered.push(byId.get(id));
    };

    preferredTrades.forEach((trade) => push(String(get(trade, 'id') ?? '')));
    sharedIds.forEach(push);
    return ordered;
  };

  const withSharedHeader = (checklist, checkpoints, preferredTrades) => ({
    ...checklist,
    tradeTypes: getSharedTrades(checkpoints, preferredTrades),
  });

  const syncTradeState = (nextItems) => {
    itemsRef.current = nextItems;
    setItems(nextItems);
  };

  // Flatten all checkpoints into a list for step-through mode
  const allCheckpoints = useMemo(() => {
    const cps = [];
    Object.keys(items).forEach((k) => {
      const check = items[k];
      if (check && check.checkpoints) {
        check.checkpoints.forEach((cp) => {
          cps.push({ ...cp, _checklist: check.checklist });
        });
      }
    });
    return cps;
  }, [items]);

  const handleClose = (opts) => {
    setStepMode(false);
    setStepIndex(0);
    setStepFormStates({});
    props.onClose(opts);
  };

  const formSubmit = async (overrideStepFormStates) => {
    try {
      if (loading) {
        return;
      }

      setLoading(true);
      if (props.onFormSubmit) {
        const finalStepStates = overrideStepFormStates || stepFormStates;
        const submitFormState = stepMode ? { ...formState, perCheckpointOverrides: finalStepStates } : formState;
        await props.onFormSubmit(submitFormState, items);
      } else {
        await Promise.all(
          props.tasks.map(async (task) => {
            const data = {
              data: {
                id: task.id,
                type: 'tasks',
                attributes: {
                  due_date: get(formState, 'dueDate'),
                  meta: get(formState, 'meta', {}),
                },
                relationships: {
                  assignees: {
                    data: get(formState, 'assignedTo', []).map((a) => ({ type: 'users', role: 'assignee', id: a.id })),
                  },
                },
              },
            };
            await sendData({
              url: `/tasks/${task.id}`,
              method: 'patch',
              data,
            });
          })
        );
      }

      setLoading(false);
      handleClose(true);
    } catch (err) {
      setError(`Error: ${err.message}`);
      setLoading(false);
    }
  };

  const defaults = {
    tradeTitle: 'Trades',
    tradeSub: 'Which trades are responsible for these tasks?',
    ...props,
  };

  useEffect(() => {
    if (!!props.open) {
      setOpenHeaderTrades({});
      const checklist = {
        // [id]: {
        //   loaded: false,
        //   checklist: {},
        //   checkpoints: [],
        // }
      };
      const selectedItems = cleanCopy(props.selectedItems);
      Object.keys({ ...selectedItems }).map((k) => {
        const item = get(selectedItems, k);
        if (item && item.itemType === 'checkpoint') {
          set(checklist, [item.checklistId, 'checklist'], item.checklist);
          if (item.subscores) {
            getInstanceSubScores(item).forEach((ss) => {
              const exists = !!get(checklist, [item.checklistId, 'checkpoints'], []).find((f) => f.id == ss.id);
              if (exists) {
                return;
              }
              set(checklist, [item.checklistId, 'checkpoints'], [...get(checklist, [item.checklistId, 'checkpoints'], []), ss]);
            });
            delete item.subscores;
          } else {
            set(checklist, [item.checklistId, 'checkpoints'], [...get(checklist, [item.checklistId, 'checkpoints'], []), item]);
          }
        }
      });

      itemsRef.current = checklist;
      setItems(checklist);
    } else {
      itemsRef.current = {};
      setItems({});
      setOpenHeaderTrades({});
      setStepMode(false);
      setStepIndex(0);
      setStepFormStates({});
    }
  }, [props.open]);

  const setLoadAndUpdateChildren = (check, sel) => {
    const checklistId = get(check, 'checklist.id');
    const currentChecklistState = get(itemsRef.current, checklistId, {});
    const loaded = get(currentChecklistState, 'loaded', false);
    const currentChecklist = get(currentChecklistState, 'checklist', get(check, 'checklist', {}));

    const checkpoints = get(currentChecklistState, 'checkpoints', []).map((checkpoint) => {
      if (loaded || size(get(checkpoint, 'tradeTypes', []))) {
        return checkpoint;
      }

      return {
        ...checkpoint,
        tradeTypes: sel,
      };
    });

    const nextItems = {
      ...itemsRef.current,
      [checklistId]: {
        ...currentChecklistState,
        loaded: true,
        checklist: withSharedHeader(currentChecklist, checkpoints, get(currentChecklist, 'tradeTypes', sel)),
        checkpoints,
      },
    };

    syncTradeState(nextItems);
  };

  const updateChecks = (check, sel, cp) => {
    const checklistId = get(check, 'checklist.id');
    const currentChecklistState = get(itemsRef.current, checklistId, {});
    const loaded = get(currentChecklistState, 'loaded', false);
    const currentChecklist = get(currentChecklistState, 'checklist', get(check, 'checklist', {}));
    const previousChecklistTrades = get(currentChecklist, 'tradeTypes', []);

    let checkpoints = get(currentChecklistState, 'checkpoints', []);
    let checklist = currentChecklist;

    if (cp) {
      checkpoints = checkpoints.map((checkpoint) => {
        if (checkpoint.id != cp.id) {
          return checkpoint;
        }

        return {
          ...checkpoint,
          tradeTypes: sel,
        };
      });
      checklist = withSharedHeader(currentChecklist, checkpoints, previousChecklistTrades);
    } else if (!loaded) {
      checklist = { ...currentChecklist, tradeTypes: sel };
    } else {
      const previousChecklistTradeIds = new Set(getTradeIds(previousChecklistTrades));
      const nextChecklistTradeIds = new Set(getTradeIds(sel));
      const addedTrades = (sel || []).filter((trade) => !previousChecklistTradeIds.has(String(get(trade, 'id'))));
      const removedTradeIds = new Set([...previousChecklistTradeIds].filter((id) => !nextChecklistTradeIds.has(id)));

      checkpoints = checkpoints.map((checkpoint) => {
        let nextTrades = get(checkpoint, 'tradeTypes', []);
        let changed = false;

        if (size(removedTradeIds)) {
          const filtered = nextTrades.filter((trade) => !removedTradeIds.has(String(get(trade, 'id'))));
          if (filtered.length !== nextTrades.length) {
            nextTrades = filtered;
            changed = true;
          }
        }

        const checkpointTradeIds = new Set(getTradeIds(nextTrades));
        const appendedTrades = addedTrades.filter((trade) => !checkpointTradeIds.has(String(get(trade, 'id'))));

        if (size(appendedTrades)) {
          nextTrades = mergeTrades(nextTrades, appendedTrades);
          changed = true;
        }

        if (!changed) {
          return checkpoint;
        }

        return {
          ...checkpoint,
          tradeTypes: nextTrades,
        };
      });

      checklist = withSharedHeader(currentChecklist, checkpoints, sel);
    }

    const nextItems = {
      ...itemsRef.current,
      [checklistId]: {
        ...currentChecklistState,
        loaded,
        checklist,
        checkpoints,
      },
    };

    syncTradeState(nextItems);
  };

  const toggleOpen = (check, _i) => {
    const key = `${check.checklist.id}_${_i}`;
    setOpen((o) => ({ ...o, [key]: !o[key] }));
  };

  const openHeaderTradeSelect = (checklistId) => {
    pendingHeaderFocusId.current = checklistId;
    setOpenHeaderTrades((current) => ({ ...current, [checklistId]: true }));
  };

  useEffect(() => {
    const checklistId = pendingHeaderFocusId.current;
    if (!checklistId) {
      return undefined;
    }

    const started = Date.now();
    const timer = window.setInterval(() => {
      const input = headerSelectRefs.current[checklistId]?.querySelector('input');
      if (input) {
        input.focus();
        pendingHeaderFocusId.current = null;
        window.clearInterval(timer);
        return;
      }
      if (Date.now() - started > 2000) {
        window.clearInterval(timer);
      }
    }, 50);

    return () => window.clearInterval(timer);
  }, [openHeaderTrades]);

  const renderChecklist = (check, i) => {
    const { checklist, checkpoints } = check;

    if (!checklist) {
      return null;
    }

    const title = () => {
      return (
        <Text type="title_medium">{`${get(checklist, 'name', '')} (${size(checkpoints)})`}</Text>
      );
    };

    const renderCP = (cp, _i) => {
      const isOpen = get(open, `${checklist.id}_${_i}`);

      return (
        <Box sx={{ width: '100%', mb: 2 }}>
          <Box onClick={() => toggleOpen(check, _i)} sx={{ cursor: 'pointer' }}>
            <CheckpointScore
              pssvCheckpoint={cp}
              checklist={checklist}
              filter={ACTIVE_SCORE_FILTER}
              showDrop={false}
              showContent={!!isOpen}
              showDisplayScore={false}
              flushContent
              headerAdornment={
                <Icon sx={{ verticalAlign: 'middle', cursor: 'pointer' }}>{isOpen ? 'arrow_drop_down' : 'arrow_right'}</Icon>
              }
            />
          </Box>
          {/* Share the checkpoint content inset (CSTopic padding: 16px left, 6px right). */}
          <Box sx={{ ml: '16px', mr: '6px' }}>
            <TradeSelect
              type="inline"
              value={cp.tradeTypes}
              checklist={{ ...checklist, checklistId: checklist.id }}
              onChange={(s) => updateChecks(check, s, cp)}
              mutedTradeIds={size(checkpoints) > 1 ? getTradeIds(get(checklist, 'tradeTypes', [])) : undefined}
              skipDefaults
            />
          </Box>
        </Box>
      );
    };

    if (!size(checkpoints)) {
      return null;
    }

    const showHeaderSelect = size(checkpoints) > 1;
    const headerTradeOpen = !!openHeaderTrades[checklist.id];

    return (
      <Box sx={{ mt: i > 0 ? 4 : 2, mb: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: showHeaderSelect ? 1 : 0 }}>
          <Box sx={{ flexShrink: 0 }}>{title()}</Box>
          {showHeaderSelect && !headerTradeOpen && (
            <IconButton size="small" color="secondary" aria-label="Set trade types" onClick={() => openHeaderTradeSelect(checklist.id)}>
              <ReadMoreIcon fontSize="small" />
            </IconButton>
          )}
          <Box
            ref={(node) => {
              headerSelectRefs.current[checklist.id] = node;
            }}
            sx={{ flex: headerTradeOpen ? 1 : undefined, minWidth: 0, ...(showHeaderSelect && headerTradeOpen ? {} : { display: 'none' }) }}
          >
            <TradeSelect
              checklist={{ ...checklist, checklistId: checklist.id }}
              value={get(checklist, 'tradeTypes', [])}
              onChecklistTradesLoaded={(s) => setLoadAndUpdateChildren(check, s)}
              onChange={(s) => updateChecks(check, s)}
              type="inline"
              placeholder={`Set ${size(checkpoints)}`}
            />
          </Box>
        </Box>

        {(checkpoints || []).map(renderCP)}
      </Box>
    );
  };

  const showTrades = get(props, 'showTrades', false);
  const showDueDate = get(props, 'showDueDate', true);
  const showTaskDefaults = get(props, 'showTaskDefaults', true);
  const { data: taskTypesData } = useTaskTypes();
  const availableTypes = props.availableTaskTypes || getAvailableTaskTypes(taskTypesData);
  const selectedTaskType = availableTypes.find((tt) => tt.id === get(formState, 'taskTypeId'));
  const showTaskTypeSelector = get(props, 'showTaskTypeSelector', false);
  const isOneByOne = get(selectedTaskType, 'multi_select_behavior') === 'one_by_one';
  const needsTaskType = showTaskTypeSelector && size(availableTypes) > 1 && !get(formState, 'taskTypeId');

  // Update closeout defaults when task type changes
  useEffect(() => {
    if (selectedTaskType && formRef.current) {
      formRef.current.updateState('meta', getCloseoutDefaults(selectedTaskType));
    }
  }, [get(selectedTaskType, 'id')]);

  const currentCheckpoint = allCheckpoints[stepIndex];
  const totalSteps = allCheckpoints.length;

  const enterStepMode = () => {
    setStepMode(true);
    setStepIndex(0);
    setStepFormStates({});
  };

  const saveCurrentStepAndAdvance = () => {
    // Validate step form before advancing
    if (stepFormRef.current) {
      const result = stepFormRef.current.validateRequired();
      if (!result.passes) return;
    }

    // Store current step's custom field values
    const cpId = get(currentCheckpoint, 'id', stepIndex);
    const updatedStepStates = {
      ...stepFormStates,
      [cpId]: {
        custom_field_values: get(formState, 'custom_field_values', {}),
      },
    };
    setStepFormStates(updatedStepStates);

    if (stepIndex < totalSteps - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      // Last step - submit all, pass updated states directly to avoid async race
      formSubmit(updatedStepStates);
    }
  };

  const renderStepContent = () => {
    if (!currentCheckpoint) return null;
    const checklist = currentCheckpoint._checklist;

    return (
      <>
        <Box sx={{ marginBottom: '16px' }}>
          <LinearProgress variant="determinate" value={((stepIndex + 1) / totalSteps) * 100} />
          <Text type="title_medium" sx={{ marginTop: '8px' }}>
            Task {stepIndex + 1} of {totalSteps}
          </Text>
        </Box>

        <Box sx={{ marginBottom: '16px', padding: '8px 0', borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Text type="label_small" sx={{ color: theme.palette.text.secondary }}>
            {get(checklist, 'name', '')}
          </Text>
          <CheckpointScore pssvCheckpoint={currentCheckpoint} checklist={checklist} filter={ACTIVE_SCORE_FILTER} showDrop={false} showContent={false} showDisplayScore={false} />
        </Box>

        {selectedTaskType && (
          <Form
            key={`step-${stepIndex}`}
            ref={stepFormRef}
            loading={loading}
            showAllScreens={true}
            isNoMarginContainer
            onFormState={(vals) => {
              setFormState((prev) => ({ ...prev, ...vals }));
            }}
            screens={[
              {
                title: '',
                inputs: [...getCustomFieldInputs(selectedTaskType, {})].filter((f) => !!f),
              },
            ]}
          />
        )}

        <ButtonContainer>
          {stepIndex > 0 && (
            <ReassignButton onClick={() => setStepIndex(stepIndex - 1)} startIcon={<Icon>arrow_back</Icon>} variant="outlined" sx={{ marginRight: '8px' }}>
              Back
            </ReassignButton>
          )}
          <ReassignButton
            onClick={saveCurrentStepAndAdvance}
            startIcon={loading ? <Loader size={16} /> : <Icon>{stepIndex < totalSteps - 1 ? 'arrow_forward' : 'check'}</Icon>}
            variant="contained"
            disabled={loading}
          >
            {stepIndex < totalSteps - 1 ? 'Next' : 'Done'}
          </ReassignButton>
        </ButtonContainer>
      </>
    );
  };

  const fullScreen = props.fullScreen ? isMobileXS : false;
  // Bulk create can shrink to its content. Floor the paper at the md breakpoint
  // when it isn't already full-screen (below md, full-screen covers the viewport).
  const paperMinWidth = !fullScreen && props.paperMinWidth ? theme.breakpoints.values[props.paperMinWidth] || props.paperMinWidth : undefined;

  return (
    <ReassignDialog
      disableEscapeKeyDown
      fullScreen={fullScreen}
      maxWidth={paperMinWidth ? false : undefined}
      sx={
        paperMinWidth
          ? { '& .MuiDialog-paper': { minWidth: `min(${paperMinWidth}px, calc(100vw - ${theme.spacing(8)}))` } }
          : undefined
      }
      open={props.open}
      onClose={() => handleClose()}
    >
      <ReassignToolbar>
        <IconButton edge="start" color="inherit" onClick={() => handleClose()} aria-label="Close" size="large">
          <CloseIcon />
        </IconButton>

        <BarTitle>
          <Title variant="h5">
            {stepMode ? `${get(selectedTaskType, 'name', 'Task')} ${stepIndex + 1} of ${totalSteps}` : props.title}
          </Title>
        </BarTitle>
      </ReassignToolbar>

      <Container className="ReassignModal-Container">
        {error && (
          <Alert
            severity="error"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setError();
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        {stepMode ? (
          renderStepContent()
        ) : (
          <>
            {(defaults.contentTitle || defaults.contentSub) && (
              <div>
                {defaults.contentTitle && <Typography variant="h5">{defaults.contentTitle}</Typography>}
                {defaults.contentSub && <Typography variant="subtitle1">{defaults.contentSub}</Typography>}
              </div>
            )}

            <div>
              <Form
                loading={loading}
                showAllScreens={true}
                onSubmit={formSubmit}
                isNoMarginContainer
                ref={formRef}
                onFormState={(vals) => {
                  if (size(availableTypes) === 1) {
                    setFormState({ ...vals, taskTypeId: get(availableTypes, '0.id') });
                  } else {
                    setFormState(vals);
                  }
                }}
                screens={[
                  {
                    title: '',
                    inputs: (() => {
                      const stdReqs = selectedTaskType ? getStandardFieldRequirements(selectedTaskType) : [];
                      const isFieldRequired = (fid) => stdReqs.find((f) => f.id === fid)?.is_required;
                      const isFieldEnabled = (fid) => {
                        const f = stdReqs.find((s) => s.id === fid);
                        return f ? f.is_enabled !== false : true;
                      };

                      // Title, reference, and media are filled in elsewhere. Description comes from each item's notes in bulk create.
                      const ASSESSMENT_SKIP = ['f_title', 'f_reference', 'f_media', 'f_description', ...(showTrades ? ['f_trade'] : [])];

                      const typeSelector = showTaskTypeSelector &&
                        size(availableTypes) > 1 && {
                          id: 'taskTypeId',
                          type: 'Select',
                          data: availableTypes.map((tt) => ({ title: tt.name, value: tt.id })),
                          defaultValue: () => get(formState, 'taskTypeId', ''),
                          props: { label: 'Task Type' },
                        };

                      const defaultFieldMap = {
                        f_description: {
                          id: 'description',
                          type: 'TextField',
                          required: isFieldRequired('f_description'),
                          defaultValue: () => get(formState, 'description', ''),
                          props: { placeholder: 'Describe your task', label: 'Description', maxRows: 5, multiline: true },
                        },
                        f_assignee: {
                          id: 'assignedTo',
                          type: 'UserSelect',
                          required: isFieldRequired('f_assignee'),
                          defaultValue: () => get(props, 'tasks.0.assignees', []),
                          data: () => props.assignees,
                          props: { label: 'Assigned To', ...get(props, 'assignedToProps', {}) },
                        },
                        f_due_date: showDueDate
                          ? {
                              id: 'dueDate',
                              type: 'KeyboardDatePicker',
                              required: isFieldRequired('f_due_date'),
                              props: { label: 'Due Date' },
                            }
                          : null,
                        f_trade: {
                          id: 'tradeTypes',
                          type: 'TradeSelect',
                          required: isFieldRequired('f_trade'),
                          defaultValue: () => get(formState, 'tradeTypes', []),
                          props: { label: 'Trade Types' },
                        },
                      };

                      const metaInput = showTaskDefaults && {
                        id: 'meta',
                        type: 'Checkbox',
                        options: selectedTaskType ? getCloseoutFromTaskType(selectedTaskType) : taskOptions(),
                        defaultValue: () => (selectedTaskType ? getCloseoutDefaults(selectedTaskType) : getAccountSetting(SETTINGS.taskDefault)),
                        props: { label: 'Requirements' },
                      };

                      const fieldOrder = selectedTaskType && get(selectedTaskType, 'field_order');

                      if (fieldOrder && Array.isArray(fieldOrder) && !isOneByOne) {
                        const customInputMap = {};
                        getCustomFieldInputs(selectedTaskType, formState).forEach((input) => {
                          const cfId = input.id.replace('custom_field_values.', '');
                          customInputMap[cfId] = input;
                        });

                        const ordered = [typeSelector];
                        fieldOrder.forEach((fid) => {
                          if (ASSESSMENT_SKIP.includes(fid)) return;
                          if (defaultFieldMap[fid] && isFieldEnabled(fid)) {
                            ordered.push(defaultFieldMap[fid]);
                          } else if (customInputMap[fid]) {
                            ordered.push(customInputMap[fid]);
                          }
                        });

                        // Append custom fields not in field_order
                        Object.keys(customInputMap).forEach((cfId) => {
                          if (!fieldOrder.includes(cfId)) {
                            ordered.push(customInputMap[cfId]);
                          }
                        });

                        ordered.push(metaInput);
                        return ordered.filter(Boolean);
                      }

                      // Fallback: no field_order or isOneByOne
                      return [
                        typeSelector,
                        defaultFieldMap.f_assignee,
                        defaultFieldMap.f_due_date,
                        ...(isOneByOne && isFieldEnabled('f_description') ? [defaultFieldMap.f_description] : []),
                        ...(selectedTaskType && !isOneByOne ? getCustomFieldInputs(selectedTaskType, formState) : []),
                        metaInput,
                      ].filter(Boolean);
                    })(),
                  },
                ]}
              />

              {showTrades && (
                <Box sx={{ mt: 6 }}>
                  <Box>
                    <Typography variant="h5">{defaults.tradeTitle}</Typography>
                    <Typography variant="subtitle1">{defaults.tradeSub}</Typography>
                  </Box>

                  {Object.keys(items).map((k, i) => renderChecklist(items[k], i))}
                </Box>
              )}

              {typeof props.extraContent === 'function' && props.extraContent({ formState, setFormState })}

              <ButtonContainer>
                {typeof props.buttons === 'function' ? (
                  props.buttons({ formState })
                ) : isOneByOne && totalSteps > 0 ? (
                  <ReassignButton
                    onClick={() => {
                      if (formRef.current) {
                        const result = formRef.current.validateRequired();
                        if (!result.passes) return;
                      }
                      enterStepMode();
                    }}
                    startIcon={<Icon>arrow_forward</Icon>}
                    variant="contained"
                    disabled={loading || needsTaskType}
                  >
                    {`Continue (${totalSteps} ${totalSteps === 1 ? 'item' : 'items'})`}
                  </ReassignButton>
                ) : (
                  <ReassignButton
                    onClick={() => {
                      if (formRef.current) {
                        const result = formRef.current.validateRequired();
                        if (!result.passes) return;
                      }
                      formSubmit();
                    }}
                    startIcon={loading ? <Loader size={16} /> : <Icon>person_add</Icon>}
                    variant="contained"
                    disabled={loading || needsTaskType}
                  >
                    {props.okButton || 'RE-ASSIGN'}
                  </ReassignButton>
                )}
              </ButtonContainer>
            </div>
          </>
        )}
      </Container>
    </ReassignDialog>
  );
};
